export interface BrandHomeLeaderboardRow {
  discovery_visibility?: unknown;
  visibility_pct?: unknown;
  top3_share_pct?: unknown;
  top3_answers_share_pct?: unknown;
  rank1_share_pct?: unknown;
  rank1_count?: unknown;
  top3_count?: unknown;
}

export interface BrandHomeTrendRow {
  date?: string | null;
  visibility_pct?: unknown;
  rank3_pct?: unknown;
  rank1_pct?: unknown;
  top3_share_pct?: unknown;
}

export interface BrandHomeWidgetSummary {
  visibilityPct: number;
  rank3Pct: number;
  rank1Pct: number;
  top3SovPct: number;
  totalSovPct: number;
  rank1Count: number;
  top3Count: number;
}

export interface BrandHomeTrendPoint {
  date: string;
  visibilityPct: number;
  rank3Pct: number;
  rank1Pct: number;
  top3SovPct: number;
}

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const roundTo = (value: number, digits: number) => {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
};

const ratioToPercent = (value: unknown) => {
  const parsed = toNumber(value);
  return parsed > 1 ? parsed : parsed * 100;
};

const trendPointSignal = (point: BrandHomeTrendPoint) =>
  point.visibilityPct + point.rank3Pct + point.rank1Pct + point.top3SovPct;

export function buildBrandHomeTrendPoints(rows: BrandHomeTrendRow[]): BrandHomeTrendPoint[] {
  const pointsByDate = new Map<string, BrandHomeTrendPoint>();

  rows.forEach(row => {
    const date = row.date?.trim();
    if (!date) return;

    const nextPoint = {
      date,
      visibilityPct: roundTo(toNumber(row.visibility_pct), 1),
      rank3Pct: roundTo(toNumber(row.rank3_pct), 1),
      rank1Pct: roundTo(toNumber(row.rank1_pct), 1),
      top3SovPct: roundTo(toNumber(row.top3_share_pct), 1),
    };
    const existingPoint = pointsByDate.get(date);

    pointsByDate.set(
      date,
      existingPoint && trendPointSignal(existingPoint) >= trendPointSignal(nextPoint)
        ? existingPoint
        : nextPoint
    );
  });

  return Array.from(pointsByDate.values()).sort(
    (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()
  );
}

export function buildBrandHomeWidgetSummary(
  row: BrandHomeLeaderboardRow | null | undefined,
  trendPoints: BrandHomeTrendPoint[] = []
): BrandHomeWidgetSummary {
  const latestPoint = trendPoints.length > 0 ? trendPoints[trendPoints.length - 1] : null;

  return {
    visibilityPct:
      latestPoint?.visibilityPct ?? roundTo(ratioToPercent(row?.discovery_visibility), 1),
    rank3Pct: latestPoint?.rank3Pct ?? roundTo(toNumber(row?.top3_answers_share_pct), 1),
    rank1Pct: latestPoint?.rank1Pct ?? roundTo(toNumber(row?.rank1_share_pct), 1),
    top3SovPct: latestPoint?.top3SovPct ?? roundTo(toNumber(row?.top3_share_pct), 1),
    totalSovPct: roundTo(toNumber(row?.visibility_pct), 1),
    rank1Count: toNumber(row?.rank1_count),
    top3Count: toNumber(row?.top3_count),
  };
}
